import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
/*eslint no-restricted-imports: ["off", {"paths": [{"name": "launchdarkly-react-client-sdk"}]}] */
import { ProviderConfig } from 'launchdarkly-react-client-sdk';
import { LDClient, initialize, LDFlagSet } from 'launchdarkly-js-client-sdk';
import { useReadLocalStorage } from 'usehooks-ts';
import { validFlags, type ValidFlags } from 'src/validFlags';

export type Flags = Partial<ValidFlags>;

type ExtraLDContext = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [attribute: string]: any;
};

const cachedLDContext: ExtraLDContext = {};

interface LaunchDarklyContext {
  flags: Flags;
  originalFlags: undefined | LDFlagSet;
  loaded: boolean;
  initializeClient: (ldContext: ExtraLDContext) => Promise<ValidFlags>;
}

export const LaunchDarklyContext = createContext<LaunchDarklyContext>({} as LaunchDarklyContext);

const processFlags = ({
  launchDarklyFlags,
  localStorageFlags,
}: {
  launchDarklyFlags: undefined | LDFlagSet;
  localStorageFlags: ValidFlags;
}): ValidFlags => {
  // start off with default flag values
  const validFlagValues: { [key: string]: boolean | string } = validFlags;

  if (launchDarklyFlags) {
    // use flag values from LD if present
    for (const key in launchDarklyFlags) {
      if (Object.keys(validFlags).includes(key)) {
        validFlagValues[key] = launchDarklyFlags[key];
      }
    }
  }

  if (
    // Overriding flag values is only allowed in non-production environments
    // Since this hook is shared between th-patient and th-provider, we need to
    // ensure both methods of detecting the prod environment are used
    String(process.env.ENVIRONMENT || '') !== 'production' && // th-patient
    top?.location.hostname !== 'athenanet.athenahealth.com' // th-provider
  ) {
    for (const key in localStorageFlags) {
      const typedKey = key as keyof typeof validFlags;
      validFlagValues[key] = localStorageFlags[typedKey];
    }
  }

  return validFlagValues as ValidFlags;
};

/**
 * This component doesn't render its children until launch darkly has finished
 * retrieving flags
 */
const LaunchDarkly: React.FC<
  WithChildren<{
    anonymous: boolean;
    ldContext?: ExtraLDContext;
    ldUserKey: string | undefined;
    clientSideId: string | undefined;
    loader?: React.ReactElement;
  }>
> = ({ anonymous, ldContext, ldUserKey, clientSideId, loader, children }) => {
  const [ldClient, setLDClient] = useState<LDClient | undefined>();
  const localStorageFlags = useReadLocalStorage('telehealthDevFlags') as ValidFlags;
  const [flags, setFlags] = useState<Flags>({});
  const refLocalStorageFlags = useRef(localStorageFlags);
  const refOriginalFlags = useRef<undefined | LDFlagSet>();
  const loaded = !!ldClient;

  /*
   * Note : any alterations that affect initialization of LaunchDarkly must be tested in accordance with the core test plan :
   * https://athenaconfluence.athenahealth.com/display/UPE/Core+LaunchDarkly+Test+Plan
   */
  const initializeClient = useCallback(
    async (ldContext: ExtraLDContext): Promise<ValidFlags> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Object.keys(ldContext).forEach((key: string) => {
        cachedLDContext[key] = ldContext[key];
      });

      const context: ProviderConfig['context'] = {
        // patient should still be anonymous at this time, but providers are not
        // the Telehealth team discussed this in our Arch Sync, and we ratified it with the URF LD admins
        anonymous,
        key: ldUserKey,
        kind: 'user',
        ...ldContext,
      };

      const client = initialize(clientSideId as string, context, {
        streaming: false,
        sendEvents: false,
        diagnosticOptOut: true,
      });

      try {
        await client.waitForInitialization();
        refOriginalFlags.current = client.allFlags();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`Error while LaunchDarkly initialize ${err}`);
      }

      const processedFlags = processFlags({
        launchDarklyFlags: refOriginalFlags.current,
        localStorageFlags: refLocalStorageFlags.current,
      });
      setFlags(processedFlags);
      setLDClient(client);

      return processedFlags;
    },
    [anonymous, ldUserKey, clientSideId]
  );

  useEffect(() => {
    initializeClient(ldContext || cachedLDContext);
  }, [initializeClient, ldContext]);

  useEffect(() => {
    if (loaded && localStorageFlags) {
      refLocalStorageFlags.current = localStorageFlags;
      const processedFlags = processFlags({
        launchDarklyFlags: refOriginalFlags.current,
        localStorageFlags: refLocalStorageFlags.current,
      });
      setFlags(processedFlags);
    }
  }, [loaded, localStorageFlags, ldContext]);

  return (
    <LaunchDarklyContext.Provider value={{ flags, originalFlags: refOriginalFlags.current, loaded, initializeClient }}>
      {loaded ? children : loader || <></>}
    </LaunchDarklyContext.Provider>
  );
};

const noopContext: LaunchDarklyContext = {
  flags: validFlags,
  originalFlags: validFlags,
  loaded: false,
  initializeClient: async () => ({}) as ValidFlags,
} as LaunchDarklyContext;

export const useFlags = (): LaunchDarklyContext => {
  const context = useContext(LaunchDarklyContext) || noopContext;
  return context;
};

export default LaunchDarkly;
